import React from 'react';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';

import Select from 'ravenjs/lib/Select';
import { parseAsNumber } from 'ravenjs/utils/number';

/**
 * Extract the select input value based on regular or multiple select
 *
 * @method getSelectInputValue
 * @param  {Event}              event    The synthetic event
 * @param  {boolean}            multiple The multiple select attr
 * @return {Any}                         The value
 */
const getSelectInputValue = (event, multiple) => {
    if (_isEmpty(event)) {
        return event;
    } else {
        return Array.isArray(event) ? event.map(option => option.value) : event.value;
    }
};

/**
 * Get the select widgets value
 *
 * @method getSelectWidgetValue
 * @param  {Object}             schema The form schema
 * @param  {Any}                value  The value for the select widget
 * @return {Any}                       The final value
 */
const getSelectWidgetValue = ({ type, items }, value) => {
    const NUMBER_INT = ['number', 'integer'];

    if (value === '') {
        return undefined;
    } else if (type === 'array' && items && NUMBER_INT.includes(items.type)) {
        return value.map(parseAsNumber);
    } else if (type === 'boolean') {
        return value === 'true';
    } else if (type === 'number') {
        return parseAsNumber(value);
    }

    return value;
};

/**
 * A simple Select component wrapped in a form-field.
 *
 * @method      ReactSelectWidget
 * @param       {Object}          props The props for the component
 * @constructor
 */
function ReactSelectWidget(props) {
    const {
        disabled,
        id,
        multiple,
        onChange,
        onBlur,
        options,
        placeholder: placeholderProps,
        required,
        schema,
        value: valueProps,
        readonly,
        formContext,
    } = props;
    const resetDefaultValue = _get(options, 'resetDefaultValue', false);
    // Fix the placeholder, when rendering inside a `<Form />` at times the
    // `placeholder` is set to an empty string if not provided in the form-schema.
    // Due to this, the `defaultProps` don't help since an empty string is a valid value.
    const placeholder = placeholderProps || '-- Select an Option --';
    // Extract out the `enumOptions` from the form field.
    const { enumOptions, menuPlacement, onChange: optionsOnChange = null } = options;
    // Find the value item in the list of `enumOptions`.
    const value = enumOptions?.find(option => option.value === valueProps) || null;
    // `onChange` handler for the `Select`.
    const _handleOnChange = event => {
        let newValue = getSelectInputValue(event);

        if (!event && resetDefaultValue) {
            newValue = resetDefaultValue;
        }

        const onChangeValue = getSelectWidgetValue(schema, newValue);
        // Fire the `onChange` passed in from the Form.
        typeof onChange === 'function' && onChange(onChangeValue);
        // Call the `onChange` handler if available from the options.
        typeof optionsOnChange === 'function' && optionsOnChange(onChangeValue);
    };
    const isSearchable = _get(options, 'isSearchable', false);
    const isClearable = _get(options, 'isClearable', false);
    const className = _get(schema, 'className');
    const showRequiredOnFormLoad = _get(schema, 'showRequiredOnFormLoad', false);

    return (
        <Select
            className={className}
            inputId={id}
            isClearable={resetDefaultValue ? resetDefaultValue !== value?.value : isClearable}
            isSearchable={isSearchable}
            isDisabled={disabled || readonly}
            isMulti={multiple}
            menuPlacement={menuPlacement}
            onChange={_handleOnChange}
            onBlur={typeof onBlur === 'function' && (event => onBlur(id, event?.target?.value))}
            options={enumOptions || []}
            placeholder={placeholder}
            required={required}
            showRequiredOnFormLoad={showRequiredOnFormLoad}
            value={value}
            formContext={formContext}
        />
    );
}

ReactSelectWidget.propTypes = {
    disabled: PropTypes.bool,
    id: PropTypes.string.isRequired,
    multiple: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func.isRequired,
    options: PropTypes.object,
    placeholder: PropTypes.string,
    required: PropTypes.bool,
    schema: PropTypes.object.isRequired,
    value: PropTypes.any,
    readonly: PropTypes.bool,
    formContext: PropTypes.object,
};

ReactSelectWidget.defaultProps = {
    disabled: null,
    multiple: null,
    options: null,
    placeholder: null,
    required: null,
    value: null,
    readonly: null,
    formContext: null,
};

export default ReactSelectWidget;
